/**
 * Location Hours Editor Component
 * Edit operating hours for each day of the week
 */

import { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { adminAPI } from '../../services/api';

export default function LocationHoursEditor({ location, onClose, onSuccess }) {
  const [hours, setHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const daysOfWeek = [
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
    { value: 0, label: 'Sunday' }
  ];

  useEffect(() => {
    fetchLocationHours();
  }, [location]);

  const fetchLocationHours = async () => {
    try {
      setLoading(true);
      const { data } = await adminAPI.locations.getById(location.id);

      // Initialize hours for all days
      const existingHours = data.hours || [];
      const allDaysHours = daysOfWeek.map(day => {
        const existing = existingHours.find(h => h.day_of_week === day.value);
        return existing || {
          day_of_week: day.value,
          open_time: '09:00',
          close_time: '17:00',
          is_closed: false
        };
      });

      setHours(allDaysHours);
    } catch (err) {
      console.error('Error fetching location hours:', err);
      setError('Failed to load location hours');

      // Initialize with defaults if fetch fails
      const defaultHours = daysOfWeek.map(day => ({
        day_of_week: day.value,
        open_time: '09:00',
        close_time: '17:00',
        is_closed: false
      }));
      setHours(defaultHours);
    } finally {
      setLoading(false);
    }
  };

  const handleHourChange = (dayOfWeek, field, value) => {
    setHours(prev => prev.map(h =>
      h.day_of_week === dayOfWeek
        ? { ...h, [field]: value }
        : h
    ));
  };

  const handleCopyToAll = () => {
    if (!hours[0]) return;

    const { open_time, close_time, is_closed } = hours[0];
    setHours(prev => prev.map(h => ({
      ...h,
      open_time,
      close_time,
      is_closed
    })));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    setSaving(true);
    try {
      await adminAPI.locations.updateHours(location.id, hours);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update location hours');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading hours...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Location Hours
            </h2>
            <p className="text-sm text-gray-600">{location.name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Copy to All Button */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleCopyToAll}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Copy Monday to All Days
            </button>
          </div>

          {/* Hours Grid */}
          <div className="space-y-3">
            {daysOfWeek.map((day, index) => {
              const dayHours = hours.find(h => h.day_of_week === day.value);
              if (!dayHours) return null;

              return (
                <div
                  key={day.value}
                  className="grid grid-cols-12 gap-4 items-center p-3 bg-gray-50 rounded-lg"
                >
                  <div className="col-span-3">
                    <label className="text-sm font-medium text-gray-700">
                      {day.label}
                    </label>
                  </div>

                  <div className="col-span-3">
                    <input
                      type="time"
                      value={dayHours.open_time}
                      onChange={(e) => handleHourChange(day.value, 'open_time', e.target.value)}
                      disabled={dayHours.is_closed}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400"
                    />
                  </div>

                  <div className="col-span-1 text-center text-gray-500">
                    to
                  </div>

                  <div className="col-span-3">
                    <input
                      type="time"
                      value={dayHours.close_time}
                      onChange={(e) => handleHourChange(day.value, 'close_time', e.target.value)}
                      disabled={dayHours.is_closed}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={dayHours.is_closed}
                        onChange={(e) => handleHourChange(day.value, 'is_closed', e.target.checked)}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700">Closed</span>
                    </label>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Hours'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
